import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibSectionHeading } from '@chewy/kib-content-groups-react'
import KibLabel from '@chewy/kib-label-react'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import { DateUtils, LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import type { InventoryOrder } from '~/api/graphql/generated/types'
import {
  getInventoryDistributors,
  getInventoryShipmentStatus,
} from '~/store/reducers/constants'

import { transformInventoryOrderForTable } from '../utils'

import styles from './OrderItemsPageHeaderLabels.module.scss'

interface OrderItemsPageHeaderLabelsProps {
  inventoryOrder: InventoryOrder
  receivedBy?: string | null
}

export const OrderItemsPageHeaderLabels = ({
  inventoryOrder: {
    orderNumber,
    orderedDate,
    distributor,
    externalCreatorFirstName,
    externalCreatorLastName,
    status: { id: statusId },
  },
  inventoryOrder,
  receivedBy,
}: OrderItemsPageHeaderLabelsProps) => {
  const { t } = useTranslation('Admin')

  const InventoryDistributors = useSelector(getInventoryDistributors)
  const distributorConstantName = Utils.getConstantName(
    distributor.id,
    InventoryDistributors,
  )
  const distributorDisplayName = LanguageUtils.getConstantTranslatedName(
    distributor.id,
    InventoryDistributors,
  )

  const InventoryShipmentStatus = useSelector(getInventoryShipmentStatus)
  const status = Utils.findById(statusId, InventoryShipmentStatus) || {}
  const statusName = LanguageUtils.getTranslatedFieldName(status)

  const EMPTY_VALUE = '--'
  const externalCreatorName =
    [externalCreatorFirstName, externalCreatorLastName]
      .filter(Boolean)
      .join(' ') || EMPTY_VALUE

  const { lastReceivedDate } = transformInventoryOrderForTable(inventoryOrder)
  const orderFields = [
    {
      label: t('Admin:CATALOG.ORDERS.ORDER_DATE'),
      value: DateUtils.formatDate(orderedDate),
    },
    {
      label: t('Admin:CATALOG.ORDERS.SUPPLIER'),
      value:
        distributorConstantName === 'Other'
          ? distributor.name
          : distributorDisplayName,
    },
    {
      label: t('Admin:CATALOG.ORDERS.ORDERED_BY'),
      value: externalCreatorName,
    },
    {
      label: t('Admin:CATALOG.ORDERS.RECEIVED_BY'),
      value: receivedBy || EMPTY_VALUE,
    },
    {
      label: t('Admin:CATALOG.ORDERS.LAST_RECEIVED'),
      value: lastReceivedDate
        ? DateUtils.formatDate(lastReceivedDate)
        : EMPTY_VALUE,
    },
  ]

  return (
    <KibContainer className={styles.kibScope}>
      <header className={styles.orderHeader}>
        <KibSectionHeading
          className={styles.orderNumber}
          heading={t('Admin:CATALOG.ORDERS.ORDER', { orderNumber })}
        />
        <KibLabel className={styles.statusLabel}>{statusName}</KibLabel>
      </header>

      <div className={styles.orderDetailsFrame}>
        <KibGrid>
          {orderFields.map(({ label, value }) => (
            <KibGridItem key={label} push="1@min-xs 1@md 1@min-lg">
              <div>{label}</div>
              <div>{value}</div>
            </KibGridItem>
          ))}
        </KibGrid>
      </div>
    </KibContainer>
  )
}
